// Accueil — tableau de bord récapitulatif (agrège buildSummary).
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MacroRings, Sparkline } from '@/components/charts';
import { InsightsCard } from '@/components/InsightsCard';
import { QuickAddBar } from '@/components/QuickAddBar';
import { ChartCard, EmptyState, Kpi, PhaseBadge, ProgressBar } from '@/components/ui';
import { BMI_LABELS, buildSummary, round } from '@/domain';
import { useConsumptions, useMilestones, useProfile, useWater, useWeights } from '@/hooks/queries';
import { useTheme } from '@/theme/ThemeContext';

const QUICK_LINKS = [
  { to: '/poids', label: '📈 Poids' },
  { to: '/calories', label: '🔥 Calories' },
  { to: '/macros', label: '🥗 Macros' },
  { to: '/hydratation', label: '💧 Hydratation' },
  { to: '/sport', label: '🏋️ Sport' },
  { to: '/analyse', label: '🔬 Analyse' },
  { to: '/journal', label: '📒 Journal' },
  { to: '/foods', label: '🍎 Aliments' },
  { to: '/data', label: '💾 Données' },
  { to: '/annexes', label: '📚 Annexes' },
  { to: '/profile', label: '⚙️ Profil' },
];

// Message du budget selon le statut (phase-aware via le statut calculé côté domaine).
const BUDGET_HINT: Record<string, (usedPct: number) => string> = {
  over: () => '⚠️ seuil dépassé',
  tight: (pct) => `serré · ${pct}% utilisé`,
  reached: () => '✅ surplus atteint',
  ok: (pct) => `${pct}% utilisé`,
};

export function HomePage() {
  const { data: profile } = useProfile();
  const { data: weights = [] } = useWeights();
  const { data: consumptions = [] } = useConsumptions();
  const { data: milestones = [] } = useMilestones();
  const { data: water = [] } = useWater();
  const { palette } = useTheme();

  const s = useMemo(
    () =>
      profile
        ? buildSummary({ profile: profile.profile, weights, consumptions, milestones, water })
        : null,
    [profile, weights, consumptions, milestones, water],
  );

  if (!s) return null;

  // En prise de masse, une hausse de poids est favorable (délta vert, libellé « Pris »).
  // En récupération, on vise l'équilibre : aucune direction de poids n'est « bonne »
  // → delta neutre (cohérent avec le delta calories et les insights de récup).
  const phase = profile?.profile.trainingPhase ?? 'cut';
  const gaining = phase === 'bulk';
  const recovering = phase === 'recovery';

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Accueil {profile && <PhaseBadge phase={profile.profile.trainingPhase} />}</h1>
          <p>Ton récapitulatif du moment.</p>
        </div>
        <div className="toolbar" style={{ margin: 0 }}>
          {QUICK_LINKS.map((l) => (
            <Link key={l.to} to={l.to} className="quick-link">
              {l.label}
            </Link>
          ))}
        </div>
      </div>

      <QuickAddBar />

      {!s.hasWeight && !s.hasConsumption ? (
        <EmptyState>
          Bienvenue ! Ajoute une pesée (onglet Poids) et une consommation (onglet Calories) pour
          voir ton récapitulatif se remplir.
        </EmptyState>
      ) : (
        <>
          <div className="grid kpis" style={{ marginBottom: '1rem' }}>
            <Kpi
              label="Poids actuel"
              to="/poids"
              value={s.hasWeight ? s.weight.currentKg : '—'}
              unit={s.hasWeight ? 'kg' : ''}
              delta={
                s.hasWeight
                  ? {
                      value: s.weight.deltaWeekKg,
                      unit: ' kg/7j',
                      goodWhenNegative: !gaining,
                      neutral: recovering,
                    }
                  : undefined
              }
            />
            <Kpi
              label="Reste avant cible"
              to="/poids"
              value={s.hasWeight ? s.weight.toTargetKg : '—'}
              unit={s.hasWeight ? 'kg' : ''}
              hint={s.hasWeight ? `${s.weight.targetProgressPct}% du chemin` : undefined}
            />
            <Kpi
              label="IMC"
              to="/poids"
              value={s.hasWeight && Number.isFinite(s.weight.bmiValue) ? s.weight.bmiValue : '—'}
              hint={
                s.hasWeight
                  ? `${BMI_LABELS[s.weight.bmiCategory]} · ne distingue pas masse grasse/musculaire`
                  : undefined
              }
            />
            <Kpi
              label="Calories du jour"
              to="/calories"
              value={s.calories.today}
              unit="kcal"
              delta={{
                value: s.calories.todayVs,
                unit: ' vs seuil',
                goodWhenNegative: phase === 'cut',
                neutral: phase === 'recovery',
              }}
            />
            <Kpi
              label={gaining ? 'Reste avant le surplus' : "Budget restant aujourd'hui"}
              to="/calories"
              value={s.budget.remaining}
              unit="kcal"
              hint={
                BUDGET_HINT[s.budget.status]?.(s.budget.usedPct) ?? `${s.budget.usedPct}% utilisé`
              }
            />
            <Kpi
              label="Adhérence"
              to="/calories"
              value={s.calories.adherencePct}
              unit="%"
              hint={`série ${s.calories.currentStreak} j · record ${s.calories.bestStreak} j`}
            />
            <Kpi
              label="Objectif atteint dans"
              to="/poids"
              value={s.weight.etaDays != null ? s.weight.etaDays : '—'}
              unit={s.weight.etaDays != null ? 'j' : ''}
              hint={`tendance ${s.weight.trendPerWeek} kg/sem`}
            />
            <Kpi
              label="Eau du jour"
              to="/hydratation"
              value={s.water.today}
              unit="ml"
              hint={`${s.water.pct}% de ${s.water.targetMl} ml`}
            />
          </div>

          {s.hasWeight && (
            <div className="card" style={{ marginBottom: '1rem' }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}
              >
                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                  Progression vers la cible
                </span>
                <span className="muted" style={{ fontSize: '0.85rem' }}>
                  {s.weight.targetProgressPct}%
                  {s.nextMilestone
                    ? ` · prochain palier : ${s.nextMilestone.label} (${s.nextMilestone.remainingKg} kg)`
                    : ''}
                </span>
              </div>
              <ProgressBar pct={s.weight.targetProgressPct} color={palette.yellow} />
            </div>
          )}

          {s.hasConsumption && (
            <div className="card" style={{ marginBottom: '1rem' }}>
              <h3>Ta semaine</h3>
              <div className="grid kpis" style={{ gap: '0.75rem', marginTop: '0.75rem' }}>
                <Kpi label="Jours suivis" value={s.weekly.daysLogged} unit="/7" />
                <Kpi label="Adhérence" value={s.weekly.adherencePct} unit="%" />
                <Kpi label="Apport moyen" value={s.weekly.avgKcal} unit="kcal/j" />
                <Kpi
                  label="Δ poids (7j)"
                  value={s.weekly.weightDeltaKg}
                  unit="kg"
                  delta={{
                    value: s.weekly.weightDeltaKg,
                    unit: '',
                    goodWhenNegative: !gaining,
                    neutral: recovering,
                  }}
                />
              </div>
              <p className="muted" style={{ fontSize: '0.85rem', marginTop: '0.6rem' }}>
                💡 {s.weekly.advice}
              </p>
            </div>
          )}

          {s.insights.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <InsightsCard insights={s.insights} />
            </div>
          )}

          <div className="grid charts">
            {s.hasWeight && (
              <ChartCard title="Tendance du poids" subtitle="moyenne mobile 7 j (derniers points)">
                <Sparkline values={s.weightSpark} color={palette.violet} height={80} />
              </ChartCard>
            )}
            {s.hasConsumption && (
              <ChartCard title="Calories récentes" subtitle="apport journalier (derniers jours)">
                <Sparkline values={s.calorieSpark} color={palette.blue} height={80} />
              </ChartCard>
            )}
            {s.hasConsumption && (
              <ChartCard
                title="Macros du jour (moyenne)"
                subtitle={`cible : ${s.macros.proteinTargetG} g P · ${s.macros.carbTargetG} g G · ${s.macros.fatTargetG} g L`}
              >
                <MacroRings
                  actual={{
                    protein: s.macros.avgProtein,
                    carbs: s.macros.avgCarbs,
                    fat: s.macros.avgFat,
                  }}
                  palette={palette}
                />
              </ChartCard>
            )}
          </div>

          {s.hasWeight && (
            <p className="muted" style={{ fontSize: '0.82rem', marginTop: '1rem' }}>
              {recovering ? 'Variation' : gaining ? 'Pris' : 'Perdu'} depuis le début :{' '}
              <strong>
                {recovering
                  ? round(-s.weight.lostKg, 1)
                  : gaining
                    ? round(-s.weight.lostKg, 1)
                    : s.weight.lostKg}{' '}
                kg
              </strong>
              {s.calories.avgKcal > 0 && (
                <>
                  {' '}
                  · apport moyen : <strong>{s.calories.avgKcal} kcal/j</strong>
                </>
              )}
            </p>
          )}
        </>
      )}
    </>
  );
}
